import type { JourneyPage } from './types'

export const reclaimWeekends: JourneyPage = {
  slug: 'reclaim-weekends',
  intro: [
    { text: "Last Saturday, you missed your daughter's ", highlight: 'football game' },
    { text: 'Sunday lunch was eaten at your ', highlight: 'desk' },
    { text: "You've forgotten what a weekend ", highlight: 'feels like' },
    { text: 'What if you got them ', highlight: 'back' },
    { text: 'Without losing a single ', highlight: 'client' },
  ],
  colorClass: 'reclaim',
  seo: {
    title: 'WealthSpace Reclaim Weekends | Adviser Work-Life Balance',
    description:
      'Stop working 60-hour weeks. Reclaim 15+ hours weekly with AI automation. Work-life balance for UK financial advisers without losing clients.',
    keywords:
      'work-life balance, adviser efficiency, time saving, automation, financial adviser productivity, reduce admin, weekend freedom, adviser burnout',
  },
  hero: {
    eyebrow: 'Picture your first WealthSpace month',
    title: 'Sundays are for',
    titleAccent: 'family, not paperwork.',
    subtitle:
      "Stop working 60-hour weeks. Stop missing your kids' football games. See how advisers are winning back 15+ hours a week without losing a single client.",
    microcopy: '',
  },
  journeySection: {
    label: 'The Story',
    title: 'From 60 hours to 45.',
    titleAccent: 'Same clients. Same income.',
    subtitle:
      "You're full. No room for more clients, more growth, more life. Here's what changes.",
  },
  timeline: [
    {
      time: 'The problem: Evenings lost to reports',
      icon: 'document',
      iconColor: 'rose',
      title: '4 hours per client report',
      meta: 'Manually building each document',
      body: 'Every suitability report means 4 hours of copying, pasting, formatting, and cross-checking. Evenings and weekends spent writing reports instead of being with family.',
      previewLabel: 'The reality',
      previewText:
        'Evenings spent at the kitchen table while your family watches TV without you. Weekends interrupted by "just one more report." The guilt of being present but not present.',
    },
    {
      time: 'The change: AI-generated first drafts',
      icon: 'sparkle',
      iconColor: 'sky',
      title: '45 minutes per report',
      meta: 'Review and refine, not write from scratch',
      body: 'WealthSpace generates the first draft from meeting notes and client data. You spend 45 minutes reviewing, adding your personal touch, and approving - instead of 4 hours writing from blank pages.',
      previewLabel: 'Time saved',
      previewText:
        '3 hours 15 minutes saved per report. With 8 reports a month, that\'s 26 hours back.',
    },
    {
      time: 'The change: Automated meeting follow-ups',
      icon: 'document',
      iconColor: 'teal',
      title: 'Instant meeting summaries',
      meta: 'Notes, actions, follow-ups - all automated',
      body: 'After every meeting, WealthSpace automatically transcribes, extracts action points, drafts follow-up emails, and updates the CRM. What used to take 30 minutes of admin after each meeting now takes zero.',
      previewLabel: 'Time saved',
      previewText:
        '30 minutes saved per meeting. With 20 meetings a month, that\'s 10 hours back.',
    },
    {
      time: 'The change: Smart task management',
      icon: 'clipboard',
      iconColor: 'violet',
      title: 'Nothing falls through the cracks',
      meta: 'AI-organised task lists and reminders',
      body: 'No more mental load of remembering everything. WealthSpace tracks every task, reminder, and follow-up. You start each day with a clear list, prioritised automatically, with time blocks already in your calendar.',
      previewLabel: 'The result',
      previewText:
        'Mental peace - no more 3am worrying about forgotten tasks.',
    },
    {
      time: 'The result: Life reclaimed',
      icon: 'users',
      iconColor: 'emerald',
      title: 'Saturday mornings at netball',
      meta: 'Present for what matters',
      body: "Imagine coaching your kid's football team on Saturdays. Sundays for family walks and roasts. The laptop stays closed all weekend - and your business actually grows.",
      previewLabel: 'The goal',
      previewText:
        'Weekends that feel like weekends. Time for the things that matter. A practice that works for you, not the other way around.',
    },
  ],
  result: {
    title: '62 hours became 43.',
    titleAccent: 'Weekends came back.',
    body: 'Same clients. Same income. Actually more referrals (because happy advisers give better service). Just 19 fewer hours of work each week.',
    stats: [
      { value: '19', label: 'Hours reclaimed per week' },
      { value: '52', label: 'Weekend days back per year' },
      { value: '0', label: 'Clients lost' },
    ],
  },
  features: {
    label: 'Where the time goes',
    title: 'Automation that',
    titleAccent: 'actually works',
    subtitle:
      'Not busy-work automation. Real time savings on the tasks that eat your evenings.',
    items: [
      {
        icon: 'document',
        title: 'Report generation',
        body: 'From 4 hours to 45 minutes. AI generates first drafts from meeting notes. You review and approve.',
      },
      {
        icon: 'mic',
        title: 'Meeting capture',
        body: 'Automatic transcription, notes, and actions. Zero admin after meetings. Walk straight to your next one.',
      },
      {
        icon: 'clock',
        title: 'Task automation',
        body: 'Tasks created automatically from meetings and client interactions. Calendar blocks added. Nothing forgotten.',
      },
    ],
  },
  testimonial: {
    quote:
      "We built WealthSpace because we were tired of missing our kids' football matches. Tired of Sunday admin sessions. Tired of the guilt. Advisers shouldn't have to choose between running a good practice and having a life.",
    authorName: 'The WealthSpace Team',
    authorRole: 'Built by Advisers, for Advisers',
    initials: 'WS',
  },
  cta: {
    title: 'Get your',
    titleAccent: 'weekends back',
    subtitle:
      "Book a demo and we'll show you exactly where you can save 15+ hours a week without losing a single client.",
    note: 'Your family misses you. Time to come back.',
  },
}
